import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { RatingResult } from '@core-api/presentation/types/response';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { ScreenReaderTable } from './ScreenReaderTable';
import { RatingGridVisualization } from './RatingGridVisualization';
import type { RatingGridChoice } from './types';

interface RatingPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  shouldHideResults: boolean;
  shouldAnimate: boolean;
  results: RatingResult[];
  choices: RatingGridChoice[];
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  range: {
    min: number;
    max: number;
  };
  xAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };
  yAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };

  backgroundImage: {
    url: string;
  } | null;
}

export const RatingGridPresentation = ({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  shouldHideResults,
  shouldAnimate,
  results,
  choices,
  CanvasVisualizationArea,
  range,
  backgroundImage,
  xAxisLabels,
  yAxisLabels,
}: RatingPresentationProps) => {
  const visibleResults = shouldHideResults
    ? []
    : results
        .filter((result) => result.average)
        .map((result) => ({
          id: result.interactiveContentChoiceId,
          average: result.average!,
          responses: result.responses,
        }));

  const ConditionalWrapper = CanvasVisualizationArea ?? Box;

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      data-testid="rating-container"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper flex="1 1 auto" width="100%">
        <RatingGridVisualization
          shouldAnimate={shouldAnimate}
          results={visibleResults}
          choices={choices}
          range={range}
          backgroundImage={backgroundImage}
          horizontalAxisLabel={xAxisLabels.axisLabel}
          verticalAxisLabel={yAxisLabels.axisLabel}
        />
      </ConditionalWrapper>
      <ScreenReaderOnly>
        <ScreenReaderTable
          results={visibleResults}
          choices={choices}
          shouldHideResults={shouldHideResults}
          range={range}
          xAxisLabel={xAxisLabels.axisLabel}
          yAxisLabel={yAxisLabels.axisLabel}
        />
      </ScreenReaderOnly>
    </Box>
  );
};
